import { OnboardingLayout } from "Components/Layout/OnboardingLayout";
import type { OnboardingStep } from "Components/Onboarding/BusinessEntity/constants";
import { useOnboardingResumeStep } from "Components/Onboarding/BusinessEntity/Hooks/useOnboardingResumeStep";
import { OnboardingWizard } from "Components/Onboarding/BusinessEntity/OnboardingWizard";
import type { StepComponent } from "Components/Onboarding/BusinessEntity/utils";
import { AddRoleBankAccountStep } from "Components/Roles/Onboarding/AddRoleBankAccountStep";
import { AddRoleLicenseSelectionStep } from "Components/Roles/Onboarding/AddRoleLicenseSelectionStep";
import { AddRoleManualLicensePaymentStep } from "Components/Roles/Onboarding/AddRoleManualLicensePaymentStep";
import { SwitchToCustomerButton } from "Components/Roles/Onboarding/SwitchToCustomerButton";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { PartyType } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useSearchParams } from "Navigation/useSearchParams";
import { Resources, useResource } from "Translations/Resources";

/**
 * The add-role wizard: the shared onboarding steps minus person KYC (the FullMoone Customer
 * finished that long ago). The last step of each flow activates the role directly — there is no
 * separate completion screen and no verification submit.
 */
type AddRoleWizardRole =
  | typeof AccessRoleCodes.Hunter
  | typeof AccessRoleCodes.HunterPlus
  | typeof AccessRoleCodes.Merchant;

const STEP_OVERRIDES: Partial<Record<OnboardingStep, StepComponent>> = {
  // The shared bank step has no submit (the legacy flow ends there); the add-role variant
  // completes the role once the context has a connected account.
  bankAccountConnection: AddRoleBankAccountStep,
  manualLicensePayment: AddRoleManualLicensePaymentStep,
  // The hunter's affiliate code is attached to the Onboarding role on the license step.
  licenseSelection: AddRoleLicenseSelectionStep,
};

const getAddRoleFlow = (
  role: AddRoleWizardRole,
  partyType: PartyType,
): ReadonlyArray<OnboardingStep> => {
  const isLegalEntity = partyType === PartyType.LegalEntity;

  if (role === AccessRoleCodes.Merchant) {
    return [
      "businessInformation",
      "incomeInformation",
      "facilityCompletion",
      ...(isLegalEntity ? (["beneficialOwners"] as const) : []),
      "bankAccountConnection",
    ];
  }

  return [
    "businessInformation",
    "incomeInformation",
    ...(isLegalEntity ? (["beneficialOwners"] as const) : []),
    "licenseSelection",
    "manualLicensePayment",
  ];
};

type Props = {
  role: AddRoleWizardRole;
  partyType: PartyType;
};

export const AddRoleOnboarding = ({ role, partyType }: Props) => {
  const { t } = useResource();
  const flow = getAddRoleFlow(role, partyType);
  const [searchParams] = useSearchParams();
  const resumeStep = useOnboardingResumeStep(flow);

  if (resumeStep.isLoading) {
    return (
      <OnboardingLayout
        title={t(Resources.Onboarding.LoadingTitle)}
        leaveButton={<SwitchToCustomerButton />}
      >
        <CenteredCircularProgress />
      </OnboardingLayout>
    );
  }

  // The bank OAuth roundtrip remounts the wizard (step lives in client state only). The callback
  // param proves the bank just connected, so resume on the bank step instead of step 1 — it now
  // shows the connected state and the complete-role submit.
  const isResumingAfterBankConnection =
    searchParams.has("connectedBankEnvironment") &&
    flow.includes("bankAccountConnection");

  return (
    <OnboardingWizard
      flow={flow}
      initialStep={
        isResumingAfterBankConnection
          ? "bankAccountConnection"
          : resumeStep.initialStep
      }
      stepOverrides={STEP_OVERRIDES}
      leaveButton={<SwitchToCustomerButton />}
    />
  );
};
